import React, { useState } from 'react';
import {
  Modal,
  Upload,
  Select,
  Input,
  List,
  Card,
  App,
  Progress,
  Button,
  Space,
  Row,
  Col,
  Spin,
} from 'antd';
import {
  InboxOutlined,
  DeleteOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { UploadFile } from 'antd/es/upload/interface';
import type { AuditPlot } from '../../../types/audit';
import { photoService } from '../../../services/api/photoService';
import { compressImage, formatBytes } from '../../../utils/imageCompress';

const { Dragger } = Upload;
const { TextArea } = Input;

interface PhotoUploadModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  auditId: number;
  plots: AuditPlot[];
}

interface PhotoItem {
  uid: string;
  file: File;
  plotId: string;
  description: string;
  preview: string;
  /** Byte size of the file the user picked. We keep it around so the
   *  card can show "X.XX MB → Y.YY MB" after compression. */
  originalSize: number;
  /** True when `file` is the compressed result, not the raw upload. */
  compressed: boolean;
}

/**
 * PhotoUploadModal Component
 * Modal for uploading multiple photos and assigning them to audit plots
 */
export const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  visible,
  onClose,
  onSuccess,
  auditId,
  plots,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressing, setCompressing] = useState(false);

  // Reset state when modal closes
  const handleClose = () => {
    setPhotos([]);
    setUploadProgress(0);
    onClose();
  };

  /**
   * Validate image file type and size
   */
  const validateFile = (file: File): boolean => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      message.error(
        t('photo.invalid_type') || 'Solo se permiten archivos JPG, JPEG y PNG'
      );
      return false;
    }

    // Hard cap: 30 MB raw. Anything between 10 MB and 30 MB gets
    // compressed client-side (see `compressImage`); below 10 MB we
    // keep the original bytes. Above 30 MB we refuse — modern phone
    // photos rarely exceed that, and decoding huge files on
    // low-power devices can OOM the tab.
    const maxSize = 30 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error(
        t('photo.file_too_large') ||
          'El tamaño del archivo no puede exceder 30MB',
      );
      return false;
    }

    return true;
  };

  /**
   * Handle file selection from Upload component.
   *
   * Each picked file goes through `compressImage` before being added
   * to state, so the eventual upload carries the already-shrunk
   * bytes. Files smaller than ~400 KB or unsupported formats are
   * passed through unchanged. We run the compressions in parallel
   * with Promise.all so picking 20 photos doesn't stall the UI.
   */
  const handleFileChange = async (info: any) => {
    const { fileList } = info;
    setCompressing(true);

    const candidates = fileList
      .map((f: UploadFile) => ({ uploadFile: f, file: f.originFileObj as File | undefined }))
      .filter(({ file }: any) => file && validateFile(file));

    let totalSaved = 0;
    try {
      const compressed = await Promise.all(
        candidates.map(async ({ uploadFile, file }: any) => {
          const res = await compressImage(file);
          totalSaved += res.originalSize - res.compressedSize;
          const preview = URL.createObjectURL(res.file);
          return {
            uid: uploadFile.uid,
            file: res.file,
            plotId: '',
            description: '',
            preview,
            originalSize: res.originalSize,
            compressed: !res.same,
          } as PhotoItem;
        }),
      );
      setPhotos(compressed);
      if (totalSaved > 0) {
        message.info(
          t(
            'photo.compress_savings',
            'Imágenes optimizadas — ahorrados {{saved}} en la subida',
            { saved: formatBytes(totalSaved) },
          ),
        );
      }
    } finally {
      setCompressing(false);
    }
  };

  /**
   * Handle plot selection for a photo
   */
  const handlePlotChange = (uid: string, plotId: string) => {
    setPhotos((prevPhotos) =>
      prevPhotos.map((photo) =>
        photo.uid === uid ? { ...photo, plotId } : photo
      )
    );
  };

  /**
   * Handle description change for a photo
   */
  const handleDescriptionChange = (uid: string, description: string) => {
    setPhotos((prevPhotos) =>
      prevPhotos.map((photo) =>
        photo.uid === uid ? { ...photo, description } : photo
      )
    );
  };

  /**
   * Remove a photo from the list
   */
  const handleRemovePhoto = (uid: string) => {
    setPhotos((prevPhotos) => {
      const photoToRemove = prevPhotos.find((p) => p.uid === uid);
      if (photoToRemove) {
        // Revoke preview URL to free memory
        URL.revokeObjectURL(photoToRemove.preview);
      }
      return prevPhotos.filter((p) => p.uid !== uid);
    });
  };

  /**
   * Validate that all photos have a plot assigned
   */
  const validatePhotos = (): boolean => {
    if (photos.length === 0) {
      message.error(
        t('photo.no_photos_selected') || 'Por favor seleccione al menos una foto'
      );
      return false;
    }

    const photosWithoutPlot = photos.filter((photo) => !photo.plotId);
    if (photosWithoutPlot.length > 0) {
      message.error(
        t('photo.assign_all_plots') ||
          'Por favor asigne una parcela a todas las fotos'
      );
      return false;
    }

    return true;
  };

  /**
   * Handle photo upload
   */
  const handleUpload = async () => {
    // Validate photos
    if (!validatePhotos()) {
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Prepare data for bulk upload
      const images = photos.map((photo) => photo.file);
      const plotIds = photos.map((photo) => photo.plotId);
      const descriptions = photos.map((photo) => photo.description);

      // Simulate progress (since we don't have real progress from the API)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Call bulk upload API
      const response = await photoService.bulkUploadPhotos({
        audit_id: auditId,
        images,
        plot_ids: plotIds,
        descriptions,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Handle response
      if (response.total_created > 0) {
        message.success(
          t('photo.upload_success') ||
            `${response.total_created} foto(s) subida(s) exitosamente`
        );

        // Show errors if any
        if (response.errors.length > 0) {
          response.errors.forEach((error) => {
            message.warning(
              `Foto ${error.index + 1}: ${error.error}`
            );
          });
        }

        // Clean up preview URLs
        photos.forEach((photo) => {
          URL.revokeObjectURL(photo.preview);
        });

        // Wait a bit before closing to ensure message is visible
        setTimeout(() => {
          // Close modal and refresh data
          onSuccess();
          handleClose();
        }, 500);
      } else {
        message.error(
          t('photo.upload_error') || 'Error al subir las fotos'
        );
      }
    } catch (error: any) {
      message.error(
        error.message ||
          t('photo.upload_error') ||
          'Error al subir las fotos'
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Modal
      title={
        <>
          <CameraOutlined style={{ marginRight: 8 }} />
          {t('photo.upload_photos') || 'Subir Fotografías de Parcelas'}
        </>
      }
      open={visible}
      onCancel={handleClose}
      onOk={handleUpload}
      confirmLoading={uploading}
      width={900}
      okText={t('buttons.upload') || 'Subir'}
      cancelText={t('common.cancel') || 'Cancelar'}
      // Disable the Subir button while we're still compressing or
      // when there are no photos yet — clicking mid-compression would
      // race with `setPhotos` and upload zero files.
      okButtonProps={{ disabled: photos.length === 0 || compressing }}
    >
      {/* Spinner overlay while we run the compression pass over the
          selected files. The Dragger's inline "Comprimiendo…" label
          was too subtle and got missed when the user picked a lot of
          big photos — the overlay locks the body so it's obvious
          something's happening. */}
      <Spin
        spinning={compressing}
        size="large"
        tip={t(
          'photo.compressing_tip',
          'Optimizando imágenes antes de subirlas…',
        )}
      >
      {/* File Upload Section */}
      {photos.length === 0 && (
        <>
          <Dragger
            multiple
            accept="image/jpeg,image/jpg,image/png"
            beforeUpload={() => false} // Prevent auto upload
            onChange={handleFileChange}
            showUploadList={false}
            disabled={compressing}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {compressing
                ? t('photo.compressing', 'Comprimiendo imágenes…')
                : t('photo.drag_drop') ||
                  'Haga clic o arrastre archivos a esta área para cargar'}
            </p>
            <p className="ant-upload-hint">
              {t(
                'photo.upload_hint_with_compress',
                'Soporta carga múltiple. JPG / JPEG / PNG. Máx. 30 MB por archivo. Solo se comprimen las imágenes que superen 10 MB (2560 px / JPEG 92 %).',
              )}
            </p>
          </Dragger>
        </>
      )}

      {/* Photo List Section */}
      {photos.length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <span>
                {t('photo.selected_photos') || 'Fotos seleccionadas'}:{' '}
                <strong>{photos.length}</strong>
              </span>
              <Button
                size="small"
                onClick={() => {
                  photos.forEach((photo) => {
                    URL.revokeObjectURL(photo.preview);
                  });
                  setPhotos([]);
                }}
              >
                {t('photo.clear_all') || 'Limpiar todo'}
              </Button>
            </Space>
          </div>

          <List
            grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
            dataSource={photos}
            renderItem={(photo) => (
              <List.Item>
                <Card
                  size="small"
                  cover={
                    <div
                      style={{
                        height: 200,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f0f0',
                      }}
                    >
                      <img
                        alt={photo.file.name}
                        src={photo.preview}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  }
                  actions={[
                    <Button
                      key="delete"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemovePhoto(photo.uid)}
                    >
                      {t('common.delete') || 'Eliminar'}
                    </Button>,
                  ]}
                >
                  <Card.Meta
                    title={
                      <div title={photo.file.name}>
                        <div
                          style={{
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {photo.file.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: photo.compressed ? '#52c41a' : '#8c8c8c',
                            fontWeight: 400,
                          }}
                        >
                          {photo.compressed
                            ? `${formatBytes(photo.originalSize)} → ${formatBytes(photo.file.size)}`
                            : formatBytes(photo.file.size)}
                        </div>
                      </div>
                    }
                    description={
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 500 }}>
                            {t('photo.select_plot') || 'Parcela'}
                            <span style={{ color: 'red' }}> *</span>
                          </label>
                          <Select
                            style={{ width: '100%' }}
                            placeholder={
                              t('photo.select_plot_placeholder') ||
                              'Seleccionar parcela'
                            }
                            value={photo.plotId || undefined}
                            onChange={(value) =>
                              handlePlotChange(photo.uid, value)
                            }
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as string)
                                ?.toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            virtual={false}
                            listHeight={400}
                            getPopupContainer={(trigger) => trigger.parentElement || document.body}
                          >
                            {plots.map((plot) => (
                              <Select.Option key={plot.id} value={plot.id}>
                                {plot.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 500 }}>
                            {t('photo.description') || 'Descripción (opcional)'}
                          </label>
                          <TextArea
                            rows={2}
                            placeholder={
                              t('photo.description_placeholder') ||
                              'Agregar descripción...'
                            }
                            value={photo.description}
                            onChange={(e) =>
                              handleDescriptionChange(photo.uid, e.target.value)
                            }
                            maxLength={500}
                          />
                        </div>
                      </Space>
                    }
                  />
                </Card>
              </List.Item>
            )}
          />
        </>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={uploadProgress} status="active" />
          <p style={{ textAlign: 'center', marginTop: 8 }}>
            {t('photo.uploading') || 'Subiendo fotografías...'}
          </p>
        </div>
      )}
      </Spin>
    </Modal>
  );
};
